/**
 * =============================================================================
 * userMessages — Fallback de mensagens comerciais para o checkout público
 * RELEASE: 2026-06-20 v1.0.0 — Sempre que a Edge não devolver `user_message`
 *                              (ex.: queda total, 5xx real, timeout de rede),
 *                              este mapa garante mensagem amigável em PT-BR.
 * =============================================================================
 */

/** Códigos de erro normalizados pelo backend. */
export type CheckoutErrorCode =
  | 'CARD_DECLINED'
  | 'CARD_INVALID_DATA'
  | 'CARD_EXPIRED'
  | 'CARD_INVALID_CVV'
  | 'CARD_BLOCKED'
  | 'INSUFFICIENT_FUNDS'
  | 'METHOD_NOT_ALLOWED'
  | 'PROVIDER_UNAVAILABLE'
  | 'RATE_LIMITED'
  | 'INVALID_REQUEST'
  | 'PAYMENT_NOT_FOUND'
  | 'PAYMENT_ERROR_GENERIC'
  | 'NETWORK_ERROR';

/** Mensagens fallback em PT-BR. */
const FALLBACK: Record<CheckoutErrorCode, string> = {
  CARD_DECLINED:
    'Pagamento recusado pela operadora do cartão. Tente outro cartão ou entre em contato com o seu banco.',
  CARD_INVALID_DATA:
    'Não foi possível validar os dados do cartão. Confira número, validade e CVV e tente novamente.',
  CARD_EXPIRED:
    'O cartão informado está vencido. Utilize outro cartão para concluir o pagamento.',
  CARD_INVALID_CVV:
    'O código de segurança (CVV) informado é inválido. Confira no verso do cartão e tente novamente.',
  CARD_BLOCKED:
    'Não foi possível concluir o pagamento por motivo de segurança. Utilize outro cartão ou tente outro meio de pagamento.',
  INSUFFICIENT_FUNDS:
    'Saldo ou limite insuficiente no cartão. Tente outro cartão ou outro meio de pagamento.',
  METHOD_NOT_ALLOWED:
    'Este link de pagamento não aceita esse meio. Selecione outra forma de pagamento disponível.',
  PROVIDER_UNAVAILABLE:
    'Estamos com instabilidade momentânea na operadora de pagamentos. Aguarde alguns instantes e tente novamente.',
  RATE_LIMITED:
    'Muitas tentativas em pouco tempo. Aguarde alguns instantes e tente novamente.',
  INVALID_REQUEST:
    'Não foi possível processar a sua solicitação. Atualize a página e tente novamente.',
  PAYMENT_NOT_FOUND:
    'Este link de pagamento não está mais disponível. Solicite um novo link ao responsável pela cobrança.',
  PAYMENT_ERROR_GENERIC:
    'Não foi possível concluir o pagamento no momento. Tente novamente em instantes.',
  NETWORK_ERROR:
    'Não conseguimos conectar à operadora de pagamentos. Verifique sua conexão e tente novamente.',
};

/** Devolve mensagem amigável pelo código. */
export function getFallbackMessage(code?: string | null): string {
  if (!code) return FALLBACK.PAYMENT_ERROR_GENERIC;
  return FALLBACK[code as CheckoutErrorCode] ?? FALLBACK.PAYMENT_ERROR_GENERIC;
}

/** Mensagem genérica para falhas de rede / SDK do Supabase Functions. */
export function getNetworkErrorMessage(): string {
  return FALLBACK.NETWORK_ERROR;
}
